import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ens_normalize } from "@adraffy/ens-normalize";
import app from "../../../../global-account/app.js";
import { toast, useAuthentication, useB3Config, useModalStore, useProfile } from "../../../../global-account/react/index.js";
import { validateImageUrl } from "../../../../global-account/react/utils/profileDisplay.js";
import { formatUsername } from "../../../../shared/utils/index.js";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import { useFirstEOA } from "../../hooks/useFirstEOA.js";
import { IPFSMediaRenderer } from "../IPFSMediaRenderer/IPFSMediaRenderer.js";
const SettingsProfileCard = () => {
  const account = useActiveAccount();
  const {
    address: eoaAddress
  } = useFirstEOA();
  const {
    data: profile,
    refetch: refreshProfile
  } = useProfile({
    address: eoaAddress || account?.address,
    fresh: true
  });
  const {
    partnerId,
    authStrategy
  } = useB3Config();
  const isBetterAuth = authStrategy === "better-auth";
  const {
    user,
    setUser
  } = useAuthentication(partnerId);
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setB3ModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);
  const navigateBack = useModalStore(state_1 => state_1.navigateBack);
  // State for inline username editing
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editedUsername, setEditedUsername] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const avatarSrc = validateImageUrl(user?.avatar) || validateImageUrl(profile?.avatar);
  // Get current username - prioritize user.username, fallback to profile data
  const currentUsername = user?.username || profile?.displayName || formatUsername(profile?.name || "");
  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingUsername && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingUsername]);
  const handleEditAvatar = () => {
    setB3ModalOpen(true);
    setB3ModalContentType({
      type: "avatarEditor",
      onSuccess: () => {
        // navigate back on success
        navigateBack();
      }
    });
  };
  const handleEditUsername = () => {
    setEditedUsername(currentUsername || "");
    setIsEditingUsername(true);
  };
  const handleCancelEdit = () => {
    setIsEditingUsername(false);
    setEditedUsername("");
  };
  const handleSaveUsername = async () => {
    if (!editedUsername.trim()) {
      toast.error("Username cannot be empty");
      return;
    }
    if (editedUsername === currentUsername) {
      // No change, just exit edit mode
      handleCancelEdit();
      return;
    }
    setIsSaving(true);
    try {
      let updatedUser;
      if (isBetterAuth) {
        // Better Auth: register username without wallet signing (DB-only, no ENS)
        // Skip ens_normalize — it rejects underscores/mixed-case that are valid non-ENS usernames
        const sanitizedUsername = editedUsername.trim().toLowerCase();
        // Type assertion needed: API now accepts message/hash as optional for Better Auth users
        updatedUser = await app.service("users").registerUsername({
          username: sanitizedUsername
        }, {});
      } else {
        // Thirdweb: register username with wallet signature + on-chain ENS
        const sanitizedUsername_0 = ens_normalize(editedUsername.trim());
        const b3Username = `${sanitizedUsername_0}.b3.fun`;
        const usernameSignMessage = `Register "${b3Username}"`;
        const usernameSignature = await account?.signMessage({
          message: usernameSignMessage
        });
        if (!usernameSignature) {
          throw new Error("Failed to sign username registration message");
        }
        updatedUser = await app.service("users").registerUsername({
          username: sanitizedUsername_0,
          message: usernameSignMessage,
          hash: usernameSignature
        }, {});
      }
      // Update user state - registerUsername returns an array with single user
      setUser(Array.isArray(updatedUser) ? updatedUser[0] : updatedUser);
      // Refresh profile to get updated data
      await refreshProfile();
      toast.success("Username updated successfully!");
      setIsEditingUsername(false);
      setEditedUsername("");
    } catch (error) {
      console.error("Error updating username:", error);
      toast.error("Failed to update username. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  const handleKeyDown = e => {
    if (e.key === "Enter") {
      handleSaveUsername();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };
  return _jsxs("div", {
    className: "flex w-full items-center gap-3",
    children: [_jsxs("div", {
      className: "relative shrink-0",
      children: [_jsx(IPFSMediaRenderer, {
        src: avatarSrc,
        alt: "Profile",
        className: "border-b3-line dark:border-white/8 size-14 rounded-full border object-cover"
      }), _jsx("button", {
        onClick: handleEditAvatar,
        className: "border-b3-line absolute -bottom-0.5 -right-0.5 flex size-[18px] items-center justify-center rounded-full border-[1.5px] bg-[#a0a0ab] transition-colors hover:bg-[#a0a0ab]/80 dark:border-white",
        "aria-label": "Edit avatar",
        children: _jsx(Pencil, {
          size: 10,
          className: "text-white",
          strokeWidth: 2.5
        })
      })]
    }), _jsx("div", {
      className: "flex shrink-0 flex-col gap-1",
      children: isEditingUsername ? (/* Edit mode - inline input */
      _jsxs("div", {
        className: "flex items-center gap-2",
        children: [_jsx("input", {
          ref: inputRef,
          type: "text",
          value: editedUsername,
          onChange: e_0 => setEditedUsername(e_0.target.value),
          onKeyDown: handleKeyDown,
          disabled: isSaving,
          className: "border-b3-line bg-b3-background text-b3-grey placeholder:text-b3-foreground-muted font-neue-montreal-medium focus:border-b3-primary-blue text-md w-full rounded-md border px-2 py-1 leading-none transition-colors focus:outline-none disabled:opacity-50",
          placeholder: "Enter username"
        }), _jsxs("div", {
          className: "flex items-center gap-1",
          children: [_jsx("button", {
            onClick: handleSaveUsername,
            disabled: isSaving,
            className: "text-b3-primary-blue hover:text-b3-primary-blue/80 flex items-center justify-center rounded-md p-1 transition-colors disabled:opacity-50",
            "aria-label": "Save username",
            children: isSaving ? _jsx(Loader2, {
              size: 18,
              className: "animate-spin"
            }) : _jsx(Check, {
              size: 18,
              strokeWidth: 2.5
            })
          }), _jsx("button", {
            onClick: handleCancelEdit,
            disabled: isSaving,
            className: "text-b3-foreground-muted hover:text-b3-grey flex items-center justify-center rounded-md p-1 transition-colors disabled:opacity-50",
            "aria-label": "Cancel editing",
            children: _jsx(X, {
              size: 18,
              strokeWidth: 2.5
            })
          })]
        })]
      })) : (/* Display mode */
      _jsxs(_Fragment, {
        children: [_jsx("div", {
          className: "flex items-center gap-1",
          children: _jsx("p", {
            className: "b3-modal-username font-neue-montreal-semibold text-lg leading-none text-[#0B57C2]",
            children: currentUsername || user?.email
          })
        }), _jsx("button", {
          onClick: handleEditUsername,
          className: "flex items-center justify-center gap-1 text-left transition-opacity hover:opacity-80",
          children: _jsx("p", {
            className: "font-inter text-sm font-semibold leading-5 text-[#51525C] dark:text-white",
            children: "Edit Username"
          })
        })]
      }))
    })]
  });
};
export default SettingsProfileCard;